import React, { FC, useEffect, useRef } from 'react';
import styled from 'styled-components';
import SelectSearch from './SelectSearch';
import Checkbox from '../../Common/Components/Checkbox';
import { ReactComponent as DownAngle } from '../../../assets/img/icons/DownAngle.svg';
import { ReactComponent as Tick } from '../../../assets/img/icons/tick.svg';
import useVisible from './useVisible';
import {
  setDailyIqFilter,
  setWeeklyAccountInsightsFilter,
  resetSelectedCampaigns,
  resetSelectedEmails,
  selectAllCampaigns,
  resetSelectedWorkflows,
  resetTestEmailAssetsFilter,
  setTestEmailAssetsFilter,
  setAssetsFilter,
} from '../Dashboard/actions';
import { DropdownListItem } from './DropdownListItem';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import WindowScroller from './WindowScroller';
import { Spin, Typography } from 'antd';

export enum DropDownTypeEnum {
  EMAIL = 'Emails',
  CAMPAIGN = 'Campaigns',
  WORKFLOW = 'Workflows',
}

interface ISelectFilterWithIcon {
  label?: string;
  icon?: string;
  searchTitle?: string;
  filterTitle?: string;
  items?: any[];
  filters: {
    isAllCampaigns?: boolean;
    isCustom?: boolean;
    isHybrid?: boolean;
    isAllType?: boolean;
    isActive?: boolean;
    isInactive?: boolean;
    isAIEmail?: boolean;
  };
  isCustom?: boolean;
  isHybrid?: boolean;
  setCheckBox?: any;
  setSelected?: any;
  selectedItems?: any[];
  assetSelectedMessage?: string;
  campaignSelectedMessage?: string;
  onSearchInput?: any;
  onClearSearch?: any;
  dropdownClass?: string;
  dropdownType: 'Emails' | 'Campaigns' | 'Workflows';
  onClose?: any;
  showSelectAll?: boolean;
  disableSelectAll?: boolean;
  handleSelectAll?: (flag: boolean) => void;
  setShareProfileFilter?: any;
  setWorkflowFilter?: any;
  isShareProfile?: boolean;
  showSentBy?: boolean;
  idPrefix?: string;
  hideShareProfile?: boolean;
  emptyMessage?: string;
  showEmptyMessage?: boolean;
  disableSearch?: boolean;
  disableAll?: boolean;
  disableDropdown?: boolean;
  isDailyIQ?: boolean;
  isWeeklyAccountInsights?: boolean;
  isWorkflow?: boolean;
  hideDailyIQ?: boolean;
  hideWeeklyAccountInsights?: boolean;
  hideWorkflow?: boolean;
  hideSearch?: boolean;
  setAllCampaignsCheck?: (val: boolean) => void;
  loading: boolean;
  disableDigestEmails?: boolean;
  disableAdhocEmails?: boolean;
  disableRows?: boolean;
  isTestEmailFilter?: boolean;
}

const SelectFilterWithIcon: FC<ISelectFilterWithIcon> = (props) => {
  const {
    items,
    filters,
    onSearchInput,
    setCheckBox,
    setSelected,
    selectedItems,
    assetSelectedMessage,
    campaignSelectedMessage,
    onClearSearch,
    dropdownClass,
    dropdownType,
    onClose,
    showSelectAll,
    disableSelectAll,
    handleSelectAll,
    setShareProfileFilter,
    setWorkflowFilter,
    isShareProfile,
    showSentBy,
    idPrefix,
    hideShareProfile,
    emptyMessage,
    showEmptyMessage,
    disableSearch,
    disableAll,
    disableDropdown,
    disableAdhocEmails,
    disableDigestEmails,
    disableRows,
    isDailyIQ,
    isWeeklyAccountInsights,
    isWorkflow,
    hideDailyIQ,
    hideWeeklyAccountInsights,
    hideWorkflow,
    isTestEmailFilter,
    hideSearch,
    setAllCampaignsCheck,
    loading,
  } = props;
  const { Paragraph } = Typography;
  const { ref, isVisible, setIsVisible } = useVisible(false);
  const scrollRef: any = useRef(null);
  const isWorkflowChecked = dropdownType === DropDownTypeEnum.WORKFLOW;

  // performin onClose action when isVisible is false and onClose passed
  useEffect(() => {
    // scroll to top whenever reopen dropdown
    if (scrollRef?.current) {
      scrollRef.current.scrollTop = 0;
    }
    if (!isVisible && onClose) {
      onClose();
    }
  }, [isVisible]);

  useEffect(() => {
    return () => {
      if (onClearSearch) {
        onClearSearch();
      }
    };
  }, []);

  const AllCampaignsFilterCheck = useSelector((state: RootState) => state.analyticsDashboard.campaignsFilters.isAllCampaigns);

  const checkSelection = (flag: boolean) => {
    // function to check whether the items that come as props are in or not in the selected list.
    let fn;
    if (flag) {
      fn = (item: any) => selectedItems?.includes(item.id);
    } else {
      fn = (item: any) => !selectedItems?.includes(item.id);
    }
    return items?.every(fn);
  };

  const rowRenderer = ({
    index,
    key,
    style,
  }: {
    columnIndex: number;
    isScrolling: boolean;
    isVisible: boolean;
    key: string;
    index: number;
    style: React.CSSProperties;
  }) => {
    const item = items[index];

    return (
      <li
        key={key}
        style={style}
        id={`${dropdownType?.toLowerCase()}-${item?.id}`}
        onClick={() => {
          setSelected(item);
        }}
        className={`${isShareProfile || isDailyIQ || isWeeklyAccountInsights || disableRows ? 'disabled' : ''}`}
      >
        <DropdownListItem
          itemName={item?.name}
          itemId={item?.id}
          selectedItems={selectedItems}
          listParent={props.dropdownClass}
          checkboxHanlder={() => {
            setSelected(item);
          }}
          {...(showSentBy ? { sentBy: item?.sentBy, emailsDD: true } : {})}
          itemParentName={
            dropdownType === DropDownTypeEnum.EMAIL ? item?.campaignName : dropdownType === DropDownTypeEnum.WORKFLOW ? item?.status : ''
          }
          isChip={dropdownType === DropDownTypeEnum.WORKFLOW}
          dateTime={item?.dateTime}
          aiType={item.aiType}
          aiColor={item.aiColor}
          isTestEmail={item?.isTestEmail}
          aiName={item.aiName}
          subject={item.subject}
          dropdownType={dropdownType}
        />
      </li>
    );
  };

  return (
    <Wrapper isWorkflowChecked={isWorkflowChecked} className="wrapper-filter-dropdown">
      <div className="d-flex">
        <div
          className={`dropdown-selecter ${isVisible ? 'active' : ''} ${disableDropdown ? 'disabled' : ''}`}
          onClick={() => {
            setIsVisible(!isVisible);
          }}
          id={`${props.idPrefix}-dropdown`}
        >
          <div className="label">
            <img src={props.icon} alt="" />
            &nbsp;&nbsp;
            {props.label}
          </div>
          <div className="d-flex">
            <div className="seprator"></div>
            <div className="p-2">
              <DownAngle />
            </div>
          </div>
        </div>
        <div className="mt-1">
          <div style={{ marginLeft: '5px' }}>
            {' '}
            {/* Apply margin using inline style */}
            {loading && <Spin size="small" spinning={loading} />}
          </div>
        </div>
      </div>
      <div ref={ref} className={`dropdown-options ${isVisible ? 'show' : ''}`} id={`${props.idPrefix}-dropdown-wrapper`}>
        {/* Toggle class "show" to show and hide dropdown */}
        <div className="dropdown-options-inner">
          <div className={`dropdown-header ${campaignSelectedMessage || assetSelectedMessage ? 'display-error' : ''} ${hideSearch ? 'pb-10' : ''}`}>
            {
              // first checking whether there is any error message. If any,
              // then display the message. If there is no message then show the label with the tick
              campaignSelectedMessage || assetSelectedMessage ? (
                <>
                  Max 4 campaigns can be selected, to view more click on <b>All Campaigns</b>
                </>
              ) : (
                <>
                  <Tick />
                  {props.label}
                </>
              )
            }
            {!hideSearch && (
              <div className="sub-header">
                <SelectSearch
                  placeholder={props.searchTitle}
                  onSearchInput={onSearchInput}
                  {...(onClearSearch ? { onClearSearch: onClearSearch } : {})}
                  disabled={disableSearch}
                />
              </div>
            )}
          </div>
          <div ref={scrollRef} className={`dropdown-wrapper ${props.dropdownClass}`}>
            <div className="dropdown-top-filters">
              <h4>{props.filterTitle}</h4>
              <ul style={{ flexDirection: 'column' }}>
                {dropdownType === DropDownTypeEnum.CAMPAIGN && (
                  <li>
                    <label className={`${isShareProfile || isDailyIQ || isWeeklyAccountInsights || isWorkflow ? 'disable' : ''}`}>
                      <Checkbox
                        checked={filters.isAllCampaigns}
                        onClick={(e: any) => {
                          setCheckBox({ isAllCampaigns: !filters.isAllCampaigns });
                          if (e.target.checked) {
                            selectAllCampaigns();
                            setTestEmailAssetsFilter();
                          } else {
                            if (setAllCampaignsCheck) {
                              setAllCampaignsCheck(false);
                            }
                            resetSelectedCampaigns();
                            resetSelectedEmails();
                            resetSelectedWorkflows();
                          }
                        }}
                      />
                      All Campaigns
                    </label>
                  </li>
                )}
                {dropdownType === DropDownTypeEnum.EMAIL || dropdownType === DropDownTypeEnum.WORKFLOW ? (
                  <li>
                    <label className={`${isShareProfile || isDailyIQ || isWeeklyAccountInsights ? 'disable' : ''}`}>
                      <Checkbox
                        checked={filters.isAllType}
                        onClick={(e: any) => {
                          if (filters.isAllType) {
                            // To prevent double click errors
                            e.preventDefault();
                          } else {
                            setCheckBox({ isAllType: !filters.isAllType });
                          }
                        }}
                      />
                      {dropdownType === DropDownTypeEnum.WORKFLOW ? 'All' : 'All Types'}
                    </label>
                  </li>
                ) : null}
                {dropdownType === DropDownTypeEnum.EMAIL || dropdownType === DropDownTypeEnum.CAMPAIGN ? (
                  <>
                    <li>
                      <label className={`${isShareProfile || isDailyIQ || isWeeklyAccountInsights || disableAdhocEmails ? 'disable' : ''}`}>
                        <Checkbox
                          checked={filters.isCustom}
                          onClick={(e: any) => {
                            if (filters.isCustom && dropdownType !== 'Campaigns') {
                              // To prevent double click errors
                              e.preventDefault();
                            } else {
                              setCheckBox({ isCustom: !filters.isCustom });
                            }
                          }}
                        />
                        Ad hoc Emails
                      </label>
                    </li>
                    <li>
                      <label className={`${isShareProfile || isDailyIQ || isWeeklyAccountInsights || disableDigestEmails ? 'disable' : ''}`}>
                        <Checkbox
                          checked={filters.isHybrid}
                          onClick={(e: any) => {
                            if (filters.isHybrid && dropdownType !== 'Campaigns') {
                              // To prevent double click errors
                              e.preventDefault();
                            } else {
                              setCheckBox({ isHybrid: !filters.isHybrid });
                            }
                          }}
                        />
                        Digests
                      </label>
                    </li>
                    <li>
                      <label className={`${isShareProfile || isDailyIQ || isWeeklyAccountInsights || disableDigestEmails ? 'disable' : ''}`}>
                        <Checkbox
                          checked={filters.isAIEmail}
                          onClick={(e: any) => {
                            if (filters.isAIEmail && dropdownType !== 'Campaigns') {
                              // To prevent double click errors
                              e.preventDefault();
                            } else {
                              setCheckBox({ isAIEmail: !filters.isAIEmail });
                            }
                          }}
                        />
                        AI Email
                      </label>
                    </li>
                  </>
                ) : null}
                {dropdownType === DropDownTypeEnum.WORKFLOW ? (
                  <>
                    <li>
                      <label>
                        <Checkbox
                          checked={filters.isActive}
                          onClick={(e: any) => {
                            setCheckBox({ isActive: !filters.isActive });
                          }}
                        />
                        Active
                      </label>
                    </li>
                    <li>
                      <label>
                        <Checkbox
                          checked={filters.isInactive}
                          onClick={(e: any) => {
                            setCheckBox({ isInactive: !filters.isInactive });
                          }}
                        />
                        Inactive
                      </label>
                    </li>
                  </>
                ) : null}
                {hideShareProfile ? null : (
                  <li>
                    <label className={`${AllCampaignsFilterCheck || isDailyIQ || isWeeklyAccountInsights || isWorkflow ? 'disable' : ''}`}>
                      <Checkbox
                        checked={isShareProfile}
                        onClick={(e: any) => {
                          setShareProfileFilter(!isShareProfile);
                        }}
                      />
                      Shared Profiles
                    </label>
                  </li>
                )}
                {hideDailyIQ ? null : (
                  <li>
                    <label className={`${AllCampaignsFilterCheck || isShareProfile || isWeeklyAccountInsights || isWorkflow ? 'disable' : ''}`}>
                      <Checkbox
                        checked={isDailyIQ}
                        onClick={(e: any) => {
                          setCheckBox({ isHybrid: false });
                          setCheckBox({ isCustom: false });
                          resetSelectedCampaigns();
                          resetSelectedEmails();
                          resetSelectedWorkflows();
                          setDailyIqFilter(!isDailyIQ);
                        }}
                      />
                      Daily IQ
                    </label>
                  </li>
                )}
                {hideWeeklyAccountInsights ? null : (
                  <li>
                    <label className={`${AllCampaignsFilterCheck || isDailyIQ || isShareProfile || isWorkflow ? 'disable' : ''}`}>
                      <Checkbox
                        checked={isWeeklyAccountInsights}
                        onClick={(e: any) => {
                          setCheckBox({ isHybrid: false });
                          setCheckBox({ isCustom: false });
                          resetSelectedCampaigns();
                          resetSelectedEmails();
                          resetSelectedWorkflows();
                          setWeeklyAccountInsightsFilter(!isWeeklyAccountInsights);
                        }}
                      />
                      Weekly Account Insights
                    </label>
                  </li>
                )}
                {hideWorkflow ? null : (
                  <li>
                    <label className={`${AllCampaignsFilterCheck || isDailyIQ || isShareProfile || isWeeklyAccountInsights ? 'disable' : ''}`}>
                      <Checkbox
                        checked={isWorkflow}
                        onClick={(e: any) => {
                          resetSelectedCampaigns();
                          resetSelectedEmails();
                          setWorkflowFilter(e.target.checked);
                        }}
                      />
                      Workflows
                    </label>
                  </li>
                )}
              </ul>
            </div>
            {dropdownType === DropDownTypeEnum.EMAIL && isTestEmailFilter !== undefined && !isWorkflow ? (
              <div className="dropdown-top-filters dropdown-test-email-filters">
                <ul style={{ flexDirection: 'column' }}>
                  <li>
                    <label className={`${isShareProfile || isDailyIQ || isWeeklyAccountInsights || disableDigestEmails ? 'disable' : ''}`}>
                      <Checkbox
                        checked={isTestEmailFilter}
                        onClick={(e: any) => {
                          if (isTestEmailFilter && dropdownType !== 'Campaigns') {
                            resetTestEmailAssetsFilter();
                          } else {
                            setTestEmailAssetsFilter();
                          }
                        }}
                      />
                      Include Test Emails
                    </label>
                  </li>
                </ul>
              </div>
            ) : null}
            <div className="dropdown-body" id={`${props.idPrefix}-dropdown-body`}>
              {showSelectAll && (
                <div className="selectall-container">
                  <SelectButton
                    onClick={() => handleSelectAll && handleSelectAll(true)}
                    disabled={
                      isShareProfile ||
                      disableSelectAll ||
                      // selectedItems?.length === items?.length ||
                      checkSelection(true)
                    }
                  >
                    Select All
                  </SelectButton>
                  |
                  <SelectButton
                    disabled={checkSelection(false) || disableSelectAll || isShareProfile}
                    onClick={() => handleSelectAll && handleSelectAll(false)}
                  >
                    Unselect All
                  </SelectButton>
                </div>
              )}
              <DropDownType>
                <h4>{dropdownType}</h4>
                <Spin size="small" spinning={loading} />
              </DropDownType>
              {
                // not loading and data array is empty then show it.
                !loading && !items?.length && <Paragraph className={`empty-items`}>No {dropdownType} Found!</Paragraph>
              }
              <WindowScroller
                ref={scrollRef}
                rowCount={items?.length}
                rowHeight={dropdownType === DropDownTypeEnum.EMAIL || dropdownType === DropDownTypeEnum.WORKFLOW ? 74 : 36}
                rowRenderer={rowRenderer}
                rowWidth={300}
              />
            </div>
          </div>
          {showEmptyMessage && <div className="message-limit"> {emptyMessage}</div>}
        </div>
      </div>
    </Wrapper>
  );
};

export default SelectFilterWithIcon;

SelectFilterWithIcon.defaultProps = {
  hideWorkflow: true,
};

const Wrapper = styled.div<{
  isWorkflowChecked?: boolean;
}>`
  position: relative;
  .disabled {
    pointer-events: none;
    opacity: 0.5;
  }
  .dropdown-selecter {
    border: 1px solid #dadada;
    min-width: 335px;
    background: #fff;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    padding-left: 10px;
    padding-right: 2px;
    height: 38px;
    /* @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      min-width: 335px;
      border-radius: 6px;
      height: 30px;
    } */
    &:focus,
    &:active,
    &.active {
      box-shadow: 0 0 0 0.1rem rgb(40 167 69 / 50%);
    }
    .label {
      color: #7e7e7e;
      font-size: 16px;
      font-weight: 500;
      font-family: 'Roboto', sans-serif;
      flex-wrap: wrap;
      padding: 2px 8px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      /* width: 140px; */
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 13px;
      }
      img {
        margin-top: -3px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          width: 15px;
        }
      }
    }
    .seprator {
      -webkit-align-self: stretch;
      -ms-flex-item-align: stretch;
      align-self: stretch;
      background-color: #dddddd;
      margin-bottom: 8px;
      margin-top: 8px;
      width: 1px;
      box-sizing: border-box;
      + .p-2 {
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          padding-top: 3px !important;
          padding-bottom: 3px !important;
        }
      }
    }
  }
  .dropdown-options {
    background: #fff;
    position: absolute;
    top: 40px;
    left: 0px;
    z-index: 9;
    min-width: 100%;
    width: auto;
    border-radius: 8px;
    width: 275px;
    transition-duration: 0.5s;
    overflow: hidden;
    height: 0px;
    opacity: 0;
    box-shadow: 0px 5px 14px 0px rgb(0 0 0 / 28%);
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      width: 350px;
    }

    &.show {
      height: auto;
      opacity: 1;
    }
    .dropdown-options-inner {
      border: 1px solid #def6e6;
      box-shadow: 0 0 40px 0 rgb(0 0 0 / 28%);
      background: #fff;
      border-radius: 8px;
      float: left;
      width: 100%;
      .message-limit {
        float: left;
        width: 100%;
        padding: 15px;
        text-align: center;
        font-size: 16px;
        font-weight: 500;
        font-family: 'Roboto', sans-serif;
        color: #ffa112;
        background: #ebf5ee;
      }
      .dropdown-header {
        background: #def6e6;
        color: #27ce0a;
        font-weight: 500;
        font-size: 16px;
        line-height: 40px;
        font-family: 'Roboto', sans-serif;
        padding: 10px 16px 0 16px;
        border-radius: 8px 8px 0 0;
        float: left;
        width: 100%;
        svg {
          height: 12px;
          width: 16px;
          margin-right: 12px;
        }
        .sub-header {
          border: 1px solid #dadada;
          padding: 10px;
          background: #f7f7f7;
          float: left;
          margin: 10px -17px 0 -17px;
          width: calc(100% + 34px);
        }
      }
      .pb-10 {
        padding-bottom: 10px;
      }
      .display-error {
        line-height: 26px;
        color: #ffa112;
        white-space: pre-line;
      }
      .dropdown-wrapper {
        float: left;
        width: 100%;
        max-height: 450px;
        overflow-x: hidden;
        overflow-y: scroll;
        scrollbar-width: thin;
        scrollbar-color: #001760 #efefef;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          max-height: 300px;
        }
        &::-webkit-scrollbar {
          width: 5px;
          height: 5px;
          border-radius: 5px;
        }
        /* Track */
        &::-webkit-scrollbar-track {
          background: #fff;
          margin-top: 10px;
          margin-bottom: 15px;
        }
        /* Handle */

        &::-webkit-scrollbar-thumb {
          background: #001760;
        }
        &.dropdown-menu-wrapper,
        &.dropdown-menu-wrapper-v2 {
          > div {
            .ant-tooltip {
              pointer-events: none;
            }
          }
        }
      }
      .dropdown-top-filters {
        padding: 0 16px 16px 16px;
        border-bottom: 1px solid #dadada;
        float: left;
        width: 100%;
        .disable {
          pointer-events: none;
          opacity: 0.5;
        }
        h4 {
          color: #424b50;
          font-size: 14px;
          font-weight: 500;
          font-family: 'Roboto', sans-serif;
          margin: 0;
          line-height: 40px;
          width: 100%;
          float: left;
        }
        ul {
          width: 100%;
          float: left;
          li {
            list-style-type: none;
            margin: 5px 0;
            label {
              font-family: 'Roboto', sans-serif;
              color: #7e7e7e;
              font-size: 16px;
              font-weight: 500;
              cursor: pointer;
              display: block;
              padding: 5px 0;

              &:hover {
                color: #27ce0a;
              }
              > div {
                margin-top: -4px;
                @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                  margin-top: -2px;
                }
              }
            }
          }
        }
      }
      .dropdown-test-email-filters {
        padding-bottom: 0 !important;
      }
      .dropdown-body {
        padding: 0 16px;
        width: 100%;
        float: left;
        .selectall-container {
          color: #c1c1c1;
          padding-top: 10px;
          padding-bottom: 0;
          button:first-child {
            padding-left: 0;
          }
        }
        h4 {
          color: #424b50;
          font-size: 14px;
          font-weight: 500;
          font-family: 'Roboto', sans-serif;
          line-height: 1;
          width: 100%;
          float: left;
          margin-top: 10px;
          margin-bottom: 0;
        }
        > div {
          width: 100%;
          float: left;
          padding: 15px 0px 20px 0px;
          list-style-type: none;
          li {
            width: 100%;
            float: left;
            padding: 0;
            margin: 0;
            &.disabled {
              pointer-events: none;
              opacity: 0.5;
            }
            .label {
              font-family: 'Roboto', sans-serif;
              color: #7e7e7e;
              font-size: 14px;
              font-weight: 500;
              cursor: pointer;
              display: block;
              padding: 5px 0;
              line-height: 30px;
              white-space: nowrap;
              text-overflow: ellipsis;
              word-break: keep-all;
              overflow: hidden;
              width: 250px;
              min-width: 200px;
              transition-duration: 0.5s;
              padding-top: ${(props) => (!props.isWorkflowChecked ? '0px' : '7px !important')};
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                padding-top: ${(props) => (!props.isWorkflowChecked ? '0px' : '3px !important')};
              }
            }
            .tick {
              opacity: 0;
              transition-duration: 0.5s;
              margin-right: 13px;
              float: left;
              svg {
                height: 12px;
                width: 16px;
                vertical-align: baseline;
              }
            }
            .current {
              color: #27ce0a;
              opacity: 1;
            }
          }
        }
      }
    }
  }
  .empty-items {
    text-align: center;
    margin-bottom: 0;
    padding-bottom: 0 !important;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #67696a;
  }
`;

const SelectButton = styled.button`
  font-family: 'Roboto';
  font-size: 13px;
  font-weight: 500;
  color: #1ec252;
  background: #fff;
  border: 0 solid #dadada;
  padding: 5px 10px;
  .selected {
    color: #c1c1c1;
  }
  &:disabled {
    color: #c1c1c1;
  }
`;

const DropDownType = styled.div`
  display: flex;
  align-items: center;
  padding: 0 !important;
  margin-top: 10px;
  & h4 {
    width: auto !important;
    margin: 0 !important;
  }
  & span {
    margin-left: 8px;
  }
  .ant-spin-spinning {
    display: flex;
  }
`;
